import Controller, { OnBeforeCancel, OnBeforeSubmit, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, CheckboxAPI, FormAPI, InputAPI, ModalProps, ModalService, RadioGroupAPI, SelectableItem, ToastService } from '@philips-internal/oswald-ui-components';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Inject } from '@odinjs/odin';
import moment from 'moment';

import BaseController from '../../../BaseController';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { ScoreMorseResponse } from '../../score-types';
import { SimpleDialog, } from '../../../dialog-helper';
import { DialogType, ModalType, SimpleDialogParams } from '../../../../interfaces/simple-dialog';
import DialogService from '../../../../services/dialog-service';
import { FragmentNames, ScoreMorseEditAliases } from '../../../../codegen/index';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { ScoreListContext } from '../../scores-list/scores-list-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { AbortableCloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MorseRadioGroupExpressions, MorseRadioGroupId } from '../radio-group-data/morse-radio-group-expressions';
import { ApplicationRoute } from '../../../../constants/routes';

@Controller({ component: ScoreMorseEditAliases.SCORE_MORSE_ADD_LAYOUT, fragment: FragmentNames.SCORE_MORSE_EDIT })
export default class ScoreMorseCreateController extends BaseController {

  @Inject(ScoreMorseEditAliases.INPUT_SETUP_DATE)
  private setupDateInputApi!: InputAPI;

  @Inject(ScoreMorseEditAliases.FORM_SCORE_MORSE_CREATE)
  private formScoreMorseCreateAPI!: FormAPI;

  @Inject(ScoreMorseEditAliases.ACTION_DELETE_SCORE_MORSE)
  private deleteScoreApi!: ActionAPI;

  @Inject(ScoreMorseEditAliases.ACTION_REFERENCE_SCORE_MORSE)
  private referenceScoreApi!: ActionAPI;

  @Inject(ScoreMorseEditAliases.ACTION_NEW_SCORE_MORSE)
  private newScoreApi!: ActionAPI;

  @Inject(ScoreMorseEditAliases.ACTION_RELEASE_SCORE_MORSE)
  private releaseScoreApi!: ActionAPI;

  @Inject(ScoreMorseEditAliases.CHECKBOX_FALL_HISTORY_OPTION)
  private checkboxFallHistoryOption!: CheckboxAPI;

  @Inject(ScoreMorseEditAliases.CHECKBOX_ENDOVENOUS_DEVICE_OPTION)
  private checkboxHeparinOption!: CheckboxAPI;

  @Inject(ScoreMorseEditAliases.CHECKBOX_HAS_COMORBIDITY_OPTION)
  private checkboxDiaSecondaryOption!: CheckboxAPI;

  @Inject(ScoreMorseEditAliases.RADIO_GROUP_AMBULATORY_AID)
  private radioGroupAmbulatoryAid!: RadioGroupAPI

  @Inject(ScoreMorseEditAliases.RADIO_GROUP_GAIT_CHARACTERISTIC)
  private radioGroupGaitCharacteristic!: RadioGroupAPI

  @Inject(ScoreMorseEditAliases.RADIO_GROUP_MENTAL_STATE)
  private radioGroupMentalState!: RadioGroupAPI

  @OnMount({ target: ScoreMorseEditAliases.FORM_SCORE_MORSE_CREATE })
  handleFormMount(): void {
    ScoreListContext.currentTab = ScoreType.SCORE_MORSE
    const { encounterId, patientId, idScoreIndexes } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    const idScore = Number(idScoreIndexes);
    this.formScoreMorseCreateAPI.setLoading(true)
    this.loadEditionData(idScore)
    this.deleteScoreApi.setEnabled(false)
    this.newScoreApi.setEnabled(false);
    this.referenceScoreApi.setEnabled(false);
    this.releaseScoreApi.setEnabled(false);
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, patientId });
  }

  @OnUnmount({ target: ScoreMorseEditAliases.FORM_SCORE_MORSE_CREATE })
  handleUnMountForm(): void {
    this.clearAbortController();
  }

  private loadEditionData(sequenceNumber: number): void {
    const abortController = this.setAbortController();
    HttpService.get(Endpoint.SCORE_MORSE, {
      sequenceNumber
    }, {abortController}).then(({ body }) => {
      const record = body as ScoreMorseResponse;
      this.formScoreMorseCreateAPI.setLoading(true);
      this.formScoreMorseCreateAPI.validate();

      const dateEvaluation = String(record.dateEvaluation);
      const diaSecondary = BooleanUtils.isTrueValue(record.diaSecondary);
      const heparin = BooleanUtils.isTrueValue(record.heparin);
      const histFall = BooleanUtils.isTrueValue(record.histFall)

      this.setupDateInputApi.setValue(dateEvaluation);

      const formData = {
        dateEvaluation,
        diaSecondary,
        heparin,
        histFall
      }

      this.formScoreMorseCreateAPI.setData(formData)
      this.context.set({ scoreMorse: record })

      this.checkboxFallHistoryOption.setValue(histFall)
      this.checkboxHeparinOption.setValue(heparin)
      this.checkboxDiaSecondaryOption.setValue(diaSecondary)

      this.scaleRadioGroup(MorseRadioGroupId.HELP_DEAMBULAR, this.radioGroupAmbulatoryAid, MorseRadioGroupExpressions.HELP_DEAMBULAR)
      this.scaleRadioGroup(MorseRadioGroupId.WALKING_MODE, this.radioGroupGaitCharacteristic, MorseRadioGroupExpressions.WALKING_MODE)
      this.scaleRadioGroup(MorseRadioGroupId.MENTAL_STATE, this.radioGroupMentalState, MorseRadioGroupExpressions.MENTAL_STATE)

    }).catch((err) => this.showToast(err?.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formScoreMorseCreateAPI.setLoading(false);
        this.deleteScoreApi.setEnabled(true);
        this.newScoreApi.setEnabled(true);
        this.referenceScoreApi.setEnabled(true);
        this.releaseScoreApi.setEnabled(true);
      });
  }

  @OnBeforeSubmit({ target: ScoreMorseEditAliases.FORM_SCORE_MORSE_CREATE })
  handleBeforeSubmit(): void {
    const formData = this.formScoreMorseCreateAPI.getRecord();
    const { encounterId, idScoreIndexes } = this.historyService.getCurrentRouteParams();
    const dateEvaluation = moment(formData.dateEvaluation as string).utc().format(ISO_FULL_DATE_FORMAT)
    const helpDeambular = formData.helpDeambular as SelectableItem;
    const mentalState = formData.mentalState as SelectableItem;
    const walkingMode = formData.walkingMode as SelectableItem;
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const sequenceNumberMorse = Number.isNaN(parseInt(idScoreIndexes as string)) ? null : idScoreIndexes as number;

    const body = {
      dateEvaluation,
      diaSecondary: BooleanUtils.isPositiveValue(formData.diaSecondary as boolean),
      helpDeambular: helpDeambular.value,
      heparin: BooleanUtils.isPositiveValue(formData.heparin as boolean),
      histFall: BooleanUtils.isPositiveValue(formData.histFall as boolean),
      idScoreIndexes: sequenceNumberMorse,
      mentalState: mentalState.value,
      nrAttendance: encounterId as number,
      professional: user.physicalPersonId,
      walkingMode: walkingMode.value,
    }

    this.createNewMorseScore(body);
  }

  @OnClick({ target: ScoreMorseEditAliases.ACTION_DELETE_SCORE_MORSE })
  handleClickDelete(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();

    this.confirmDelete(idScoreIndexes as number);
  }

  @OnClick({ target: ScoreMorseEditAliases.ACTION_NEW_SCORE_MORSE })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    applicationContext.redirect(ApplicationRoute.SCORE_MORSE_CREATE, {
      encounterId,
      patientId,
    })
  }

  @OnClick({ target: ScoreMorseEditAliases.ACTION_RELEASE_SCORE_MORSE })
  handleClickInactivate(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    this.showModalSubmit(idScoreIndexes as number);
  }

  @OnClick({ target: ScoreMorseEditAliases.ACTION_REFERENCE_SCORE_MORSE })
  handleClickReference(): void {
    this.getReference();
  }

  private scaleRadioGroup(expressionId: number, radioGroup: RadioGroupAPI, type: string): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        radioGroup.setData(responseBody);

        const scoreMorse = this.context.get('scoreMorse') as ScoreMorseResponse;
        if (!scoreMorse || Number.isNaN(scoreMorse.idScoreIndexes)) {
          radioGroup.setValue(responseBody[0]);
          return
        }

        if (type === MorseRadioGroupExpressions.HELP_DEAMBULAR) {
          const helpDeambular = responseBody.filter(({ key }) => scoreMorse?.helpDeambular === Number(key))[0]
          radioGroup.setValue(helpDeambular)
          this.context.set({ scoreMorse: {
            ...scoreMorse,
            helpDeambular
          }})
          this.formScoreMorseCreateAPI.setData({
            ...this.formScoreMorseCreateAPI.getData(),
            helpDeambular
          })
        }

        if (type === MorseRadioGroupExpressions.MENTAL_STATE) {
          const mentalState = responseBody.filter(({ key }) => scoreMorse?.mentalState === Number(key))[0]
          radioGroup.setValue(mentalState)
          this.context.set({ scoreMorse: {
            ...scoreMorse,
            mentalState
          }})
          this.formScoreMorseCreateAPI.setData({
            ...this.formScoreMorseCreateAPI.getData(),
            mentalState
          })
        }

        if (type === MorseRadioGroupExpressions.WALKING_MODE) {
          const walkingMode = responseBody.filter(({ key }) => scoreMorse?.walkingMode === Number(key))[0]
          radioGroup.setValue(walkingMode)
          this.context.set({ scoreMorse: {
            ...scoreMorse,
            walkingMode
          }})
          this.formScoreMorseCreateAPI.setData({
            ...this.formScoreMorseCreateAPI.getData(),
            walkingMode
          })
        }
      }).catch((error) => {
        this.showToast(error?.body.errorMessage, ToastLevel.ERROR);
      });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'device-remove-validation',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private releaseMorseScore(sequenceNumber: number): void {
    this.formScoreMorseCreateAPI.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.put(Endpoint.RELEASE_SCORE_MORSE, { sequenceNumber }, {abortController})
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => {
        this.formScoreMorseCreateAPI.setLoading(false);
      });
  }

  private showModalSubmit(idScoreIndexes: number): void {
    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'patient-evolution.release-title-evolution',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.releaseMorseScore(idScoreIndexes) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    }

    SimpleDialog.openDialog(dialogParams);
  }

  private createNewMorseScore(body: Record<string, Value>): void {
    this.formScoreMorseCreateAPI.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.post(Endpoint.POST_CREATE_SCORE_MORSE, undefined, { abortController, body })
      .then((response) => {
        const {idScoreIndexes} = response.body as ScoreMorseResponse;
        this.showModalSubmit(idScoreIndexes as number);
      })
      .catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      })
      .finally(() => this.formScoreMorseCreateAPI.setLoading(false));
  }

  private getReference(): void {
    const scoreType = 'morse';
    const modalProperties: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-reference',
      id: 'score-reference',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'tws.reference-pain-scale-value',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      scoreType,
    }

    ModalService.openFullscreen(modalProperties);
  }

  private confirmDelete(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.delete(idScore) },
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  private delete(sequenceNumber: number): void {
    this.formScoreMorseCreateAPI.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.delete(Endpoint.SCORE_MORSE, {
      sequenceNumber
    }, {abortController}).then(() => {
      applicationContext.popNavigationItem();
    }).catch((err) => {
      this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
    }).finally(() => {
      this.formScoreMorseCreateAPI.setLoading(false);
    });
  }

  private discardChanges(): void {
    DialogService.open({
      descriptionDialog: 'clinical-notes.discard-changes',
      onClickCancel: () => { },
      onClickOk: () => { this.goBack() },
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit'
    });
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  @OnUnmount(ScoreMorseEditAliases.SCORE_MORSE_ADD_LAYOUT)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_MORSE;
  }

  @OnBeforeCancel(ScoreMorseEditAliases.FORM_SCORE_MORSE_CREATE)
  handleCancelForm(event: AbortableCloseEvent): void {
    event.abort();
    this.discardChanges();
  }
}
